import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-picker';
import { TimeGranularitySelect, type TimeGranularity } from '@/components/ui/time-granularity-select';
import { SimpleBarChart, DataTable, type ChartDataPoint } from '@/components/ui/simple-chart';
import { client, formatPrice } from '@vn-propane/shared';

interface GasStationRevenue {
  gas_station_id: string;
  gas_station_name: string | null;
  revenue: number;
  order_count: number;
}

interface ProductRevenue {
  product_id: string;
  product_name: string | null;
  revenue: number;
  quantity_sold: number;
}

interface ReportSummary {
  total: number;
  count: number;
  average: number;
  min: number;
  max: number;
}

interface TimeSeriesReport {
  report_type: string;
  granularity: string;
  start_date: string;
  end_date: string;
  data: ChartDataPoint[];
  summary: ReportSummary;
}

interface RevenueReport {
  time_series: TimeSeriesReport;
  by_gas_station: GasStationRevenue[];
  by_product: ProductRevenue[];
}

function getDefaultDateRange(): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

export default function RevenueReportPage() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation('common');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RevenueReport | null>(null);

  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState<Date | undefined>(defaultRange.start);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultRange.end);
  const [granularity, setGranularity] = useState<TimeGranularity>('day');

  const fetchData = useCallback(async () => {
    if (!orgId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await client.get({
        url: `/gas_admin/organizations/${orgId}/reports/revenue`,
        query: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          granularity,
        },
      });

      if (response.error) {
        setError('Failed to load revenue report');
        return;
      }

      setData((response.data as { data: RevenueReport } | undefined)?.data ?? null);
    } catch (err) {
      setError('Failed to load revenue report');
    } finally {
      setLoading(false);
    }
  }, [orgId, startDate, endDate, granularity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatRevenue = (value: number) => formatPrice(String(value), 'VND') || '';

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.filters')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startLabel={t('labels.from')}
            endLabel={t('labels.to')}
          />
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">{t('reports.granularityLabel')}:</span>
            <TimeGranularitySelect
              value={granularity}
              onChange={setGranularity}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-8 text-muted-foreground">
          {t('labels.loading')}
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Summary */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.totalRevenue')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{formatRevenue(data.time_series.summary.total)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.orders')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{data.time_series.summary.count}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.averagePerPeriod')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{formatRevenue(data.time_series.summary.average)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.maxPeriod')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{formatRevenue(data.time_series.summary.max)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle>{t('reports.revenueOverTime')}</CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart
                data={data.time_series.data}
                height={250}
                formatValue={formatRevenue}
              />
            </CardContent>
          </Card>

          {/* By Gas Station and By Product */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('reports.revenueByStation')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.by_gas_station.length === 0 ? (
                  <p className="text-muted-foreground">{t('reports.noData')}</p>
                ) : (
                  <div className="space-y-3">
                    {data.by_gas_station.map((station) => (
                      <div key={station.gas_station_id} className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">
                            {station.gas_station_name || t('reports.unknownStation')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {station.order_count} {t('reports.orders')}
                          </div>
                        </div>
                        <div className="font-medium">
                          {formatRevenue(station.revenue)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('reports.revenueByProduct')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.by_product.length === 0 ? (
                  <p className="text-muted-foreground">{t('reports.noData')}</p>
                ) : (
                  <div className="space-y-3">
                    {data.by_product.map((product) => (
                      <div key={product.product_id} className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">
                            {product.product_name || t('reports.unknownProduct')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {product.quantity_sold} {t('reports.unitsSold')}
                          </div>
                        </div>
                        <div className="font-medium">
                          {formatRevenue(product.revenue)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          <Card>
            <CardHeader>
              <CardTitle>{t('reports.dataTable')}</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={data.time_series.data}
                valueLabel={t('reports.metrics.revenue')}
                dateLabel={t('reports.tableDate')}
                countLabel={t('reports.tableCount')}
                formatValue={formatRevenue}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
